import React, { useEffect, useState } from 'react';

import Pill from './Pill';
import ProgressBar from './ProgressBar';

import { makeStyles } from '@mui/styles';

import CompletedSummary from './CompletedSummary';
import ExpandedTitleRow from './ExpandedTitleRow';
import Info from './Info';
import LockedSummary from './LockedSummary';

const useStyles = makeStyles(() => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '24px',
        width: '100%',
        padding: '32px 0 32px 0 !important',
        margin: '0 16px 0 16px !important'
    },
    lockedContainer: {
        backgroundColor: '#F6F7FB',
        borderTop: '1px solid #E8EAF4',
        borderRadius: '0 0 16px 16px',
        boxSizing: 'border-box'
    },
    lockedDescription: {
        fontSize: '16px',
        fontWeight: 400,
        lineHeight: '22px',
        color: '#A6A6BF',
        textAlign: 'center',
        padding: '0 24px'
    }
}));

const possessiveName = (memberName?: string) => {
    if (!memberName) {
        return `this member's`;
    }

    return memberName.endsWith('s') || memberName.endsWith('S')
        ? `${memberName}'`
        : `${memberName}'s`;
};

type ExtraProgressBarProps = {
    baseLimit: number;
    baseUsed: number;
    limit: number;
    mcfYearlyDepleted?: boolean;
    name?: string;
    remaining?: number;
    samaritanYearlyDepleted?: boolean;
    used: number;
};

const ExtraProgressBar: React.FC<ExtraProgressBarProps> = ({
    baseLimit,
    baseUsed,
    limit,
    mcfYearlyDepleted = false,
    name,
    remaining: assignableRemaining,
    samaritanYearlyDepleted = false,
    used
}) => {
    const classes = useStyles();
    const month = new Date().toLocaleString('default', { month: 'long' });
    const title = `Extra ${month} Bonuses`;
    const remaining = limit - used;
    const baseRemaining = baseLimit - baseUsed;
    const isLocked =
        limit > 0 &&
        baseRemaining > 0 &&
        !samaritanYearlyDepleted &&
        !mcfYearlyDepleted;
    const isCollapsable =
        limit > 0 &&
        remaining <= 0 &&
        (baseRemaining <= 0 || mcfYearlyDepleted);
    const [collapsed, setCollapsed] = useState(true);
    const [lockedCollapsed, setLockedCollapsed] = useState(true);

    useEffect(() => {
        if (isCollapsable) {
            setCollapsed(true);
        }
    }, [isCollapsable]);

    useEffect(() => {
        if (isLocked) {
            setLockedCollapsed(true);
        }
    }, [isLocked]);

    const handleExpand = () => {
        setCollapsed(false);
    };

    const handleCollapse = () => {
        setCollapsed(true);
    };

    const handleLockedExpand = () => {
        setLockedCollapsed(false);
    };

    const handleLockedCollapse = () => {
        setLockedCollapsed(true);
    };

    if (!limit) {
        return null;
    }

    if (mcfYearlyDepleted && remaining > 0) {
        return (
            <div className={classes.container}>
                <ExpandedTitleRow title={title} />
                <ProgressBar
                    remaining={0}
                    used={used}
                    limit={limit}
                />
                <Info message="This year's extra bonus funds were fully assigned." />
            </div>
        );
    }

    if (isLocked && lockedCollapsed) {
        return (
            <LockedSummary
                description={`Assign all $${baseLimit} in the base pool to unlock the extra $${limit} bonus pool`}
                onExpand={handleLockedExpand}
                pillText={`+ $${limit}`}
                title={title}
            />
        );
    }

    if (isLocked) {
        const lockedDescription = `Assign all $${baseLimit} in the base pool to unlock the extra $${limit} bonus pool`;

        return (
            <div className={`${classes.container} ${classes.lockedContainer}`}>
                <ExpandedTitleRow
                    collapsable
                    muted
                    onCollapse={handleLockedCollapse}
                    title={title}
                />
                <ProgressBar
                    muted
                    used={used}
                    limit={limit}
                />
                <Pill
                    muted
                    text="✨ Funded by Multiply Capital Foundation for limited time only ✨"
                />
                <span className={classes.lockedDescription}>
                    {lockedDescription}
                </span>
            </div>
        );
    }

    if (isCollapsable && collapsed) {
        return (
            <CompletedSummary
                onExpand={handleExpand}
                subtitle={`You've assigned all $${limit} from the extra bonus pool`}
                title={title}
            />
        );
    }

    return (
        <div className={classes.container}>
            {isCollapsable ? (
                <ExpandedTitleRow
                    collapsable
                    onCollapse={handleCollapse}
                    title={title}
                />
            ) : (
                <ExpandedTitleRow title={title} />
            )}
            <ProgressBar
                remaining={assignableRemaining}
                used={used}
                limit={limit}
            />
            {assignableRemaining != null &&
            assignableRemaining < remaining &&
            assignableRemaining > 0 && (
                <Info
                    message={`$${assignableRemaining} remaining in ${possessiveName(name)} yearly extra bonus funds`}
                />
            )}
            <Pill text="✨ Funded by Multiply Capital Foundation for limited time only ✨" />
        </div>
    );
};

export default ExtraProgressBar;
